type SectionHeadingProps = {
  children: string;
  subtitle?: string;
  /** Left is the default; centred suits full-width grids like categories. */
  align?: "start" | "center";
  className?: string;
};

/**
 * Heading for a top-level public-site section.
 *
 * Redesigned from a bare 35px navy `<h2>`. Three changes carry the new look:
 *
 * - **A tighter type scale.** The old fixed 35px was heavy on desktop and
 *   oversized on small screens; this steps 28 -> 32 -> 36px with tightened
 *   tracking, so it reads as a considered scale rather than one big size.
 * - **An accent rule** in the brand sky blue underneath, which gives each
 *   section a visible anchor. Deliberately not an eyebrow label - that would
 *   need new copy translated into both locales for no real gain.
 * - **An optional subtitle** for sections that benefit from a line of context.
 *
 * Palette follows the region: `primary` for the title, `secondary` for the rule.
 */
export default function SectionHeading({
  children,
  subtitle,
  align = "start",
  className = "",
}: SectionHeadingProps) {
  const centred = align === "center";

  return (
    <div
      className={`mb-10 flex flex-col ${centred ? "items-center text-center" : "items-start"} ${className}`}
    >
      <h2 className="text-[28px] font-bold leading-tight tracking-tight text-primary sm:text-[32px] lg:text-[36px]">
        {children}
      </h2>

      <span
        aria-hidden="true"
        className="mt-3 block h-1 w-16 rounded-full bg-secondary"
      />

      {subtitle ? (
        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-500">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}
